import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle, Button } from "@tabipla/ui";
import { useAdsInList, NativeAd, useAdContext } from "@tabipla/components";
import { Plus, Trash2, Pencil, Users } from "lucide-react";
import type { ExpenseResponseDto, GroupMemberResponseDto } from "@tabipla/shared";
import { EXPENSE_CATEGORIES, getCategoryClasses } from "@/config/categories";

const AD_SLOT_EXPENSES = import.meta.env.VITE_AD_SLOT_EXPENSES ?? "0000000000";

interface ExpenseListProps {
  expenses: ExpenseResponseDto[];
  /** グループメンバー一覧。「全員で」ボタンの表示判定に使用 */
  members: GroupMemberResponseDto[];
  onAddClick: () => void;
  onEditClick: (expense: ExpenseResponseDto) => void;
  onDeleteClick: (expenseId: string) => void;
  /**
   * 「全員で均等割り」アクション。
   * 出費日以前に参加したメンバー全員で均等に割り直す。
   * すでに全員で割っている場合はボタン非表示。
   */
  onSplitAll: (expense: ExpenseResponseDto) => void;
  canEdit: boolean;
}

export function ExpenseList({
  expenses,
  members,
  onAddClick,
  onEditClick,
  onDeleteClick,
  onSplitAll,
  canEdit,
}: ExpenseListProps) {
  const { t } = useTranslation();
  const adContext = useAdContext();

  // 広告をリストに挿入（8件ごと、5件目以降）
  const expensesWithAds = useAdsInList(expenses, {
    interval: 8,
    startAfter: 5,
    maxAds: 3,
    slotIds: [AD_SLOT_EXPENSES],
    enabled: adContext?.config.enabled ?? false,
  });

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat("ja-JP", {
      style: "currency",
      currency: "JPY",
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("ja-JP", {
      month: "short",
      day: "numeric",
    });
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-lg">{t("expenses.title")}</CardTitle>
        {canEdit && (
          <Button size="sm" onClick={onAddClick}>
            <Plus className="mr-2 h-4 w-4" />
            {t("expenses.addExpense")}
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {expenses.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            {t("expenses.noExpenses")}
          </p>
        ) : (
          <div className="space-y-2">
            {expensesWithAds.map((item) => {
              if (item.type === "ad") {
                return <NativeAd key={item.key} slotId={item.slotId} variant="inline" />;
              }

              const expense = item.data;
              const categoryConfig = EXPENSE_CATEGORIES[expense.category];
              const IconComponent = categoryConfig.icon;

              // Check if expense is already split among all eligible members
              const expenseDate = new Date(expense.date);
              const eligibleMembers = members.filter((m) => {
                const joinedAt = new Date(m.joinedAt);
                return joinedAt.toDateString() <= expenseDate.toDateString();
              });
              const isAlreadySplitAll = expense.shares.length === eligibleMembers.length;

              return (
                <div
                  key={expense.id}
                  className="rounded-xl border bg-card p-3 transition-all hover:shadow-md"
                >
                  {/* Main row: Icon + Content + Amount */}
                  <div className="flex items-center gap-3">
                    {/* Category Icon */}
                    <div
                      className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${getCategoryClasses(expense.category)}`}
                    >
                      <IconComponent className="h-5 w-5" />
                    </div>

                    {/* Title & Meta */}
                    <div className="min-w-0 flex-1">
                      <h3 className="text-sm font-semibold leading-tight">{expense.description}</h3>
                      <p className="text-sm text-foreground/70">
                        {expense.paidByName} · {formatDate(expense.date)} · {expense.shares.length}
                        {t("expenses.people", "人")}
                      </p>
                    </div>

                    {/* Amount */}
                    <div className="shrink-0 text-right">
                      <span className="text-lg font-bold tabular-nums">
                        {formatAmount(expense.amount)}
                      </span>
                    </div>
                  </div>

                  {/* Action buttons - separate row */}
                  {canEdit && (
                    <div className="mt-2 flex justify-end gap-1.5 border-t pt-2">
                      {!isAlreadySplitAll && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-9 px-3 text-sm text-emerald-600 hover:text-emerald-700"
                          onClick={(e) => {
                            e.stopPropagation();
                            onSplitAll(expense);
                          }}
                        >
                          <Users className="mr-1.5 h-4 w-4" />
                          全員で
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-9 px-3 text-sm text-muted-foreground hover:text-foreground"
                        onClick={(e) => {
                          e.stopPropagation();
                          onEditClick(expense);
                        }}
                      >
                        <Pencil className="mr-1.5 h-4 w-4" />
                        編集
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-9 px-3 text-sm text-destructive hover:text-destructive"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeleteClick(expense.id);
                        }}
                      >
                        <Trash2 className="mr-1.5 h-4 w-4" />
                        削除
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
